import { getAllDepartments } from './dato-departments'
import { DEPARTMENT_MAP } from './department-map'
import type {
	DepartmentType,
	CareerListing,
	BaseGreenhouseCareerListingResponse,
	CareerListingMetadata,
	CareerListingDepartment,
	GreenhouseQuestion,
	GreenhouseDemographicQuestion,
	ApplicationFormFieldsState,
	CustomFieldKey,
	HashiEducationQuestions,
	EducationResourceResponse,
} from './types'

export const GH_JOBS_ENDPOINT = process.env.GREENHOUSE_API_URL

const GH_PROXY_ENDPOINT = 'api/submit-gh-application'
export const COMPLIANCE_FIELD_NAME = 'data_compliance'
export const COMPLIANCE_LABEL =
	'By checking this box, I agree to allow HashiCorp to store and process my data for the purpose of considering my eligibility regarding my current application for employment'
export const MULTI_SELECT_DEFAULT = 'Select an option'

export const customFieldKeys: Array<CustomFieldKey> = [
	'Career Page Product',
	'Workplace Type',
	'Employee Type',
] as const

export const isEducationResourceResponse = (
	response: unknown
): response is EducationResourceResponse => {
	return (
		typeof response === 'object' &&
		response !== null &&
		'items' in response &&
		Array.isArray(response.items) &&
		'meta' in response &&
		typeof response.meta === 'object' &&
		response.meta !== null &&
		'per_page' in response.meta &&
		'total_count' in response.meta
	)
}

// education questions aren't part of the greenhouse API response, it only indicates whether or not the application expects education data
export const EDUCATION_QUESTIONS: HashiEducationQuestions = [
	{
		id: 'degree',
		label: 'Degree',
		required: true,
		apiResource: 'degrees',
	},
	{
		id: 'discipline',
		label: 'Discipline',
		required: true,
		apiResource: 'disciplines',
	},
	{
		id: 'school',
		label: 'School',
		required: true,
		apiResource: 'schools',
	},
	{
		id: 'start_date',
		label: 'Start Date',
		required: true,
		minYearDelta: 60,
		maxYearDelta: 6,
	},
	{
		id: 'end_date',
		label: 'End Date',
		required: true,
		minYearDelta: 60,
		maxYearDelta: 6,
	},
] as const

export type FetchCareersParams = {
	listingId: string
	questions?: boolean
	pay?: boolean
}

export async function fetchCareer({
	listingId,
	questions,
	pay,
}: FetchCareersParams): Promise<{
	career: CareerListing
}> {
	const questionsQuery = questions ? 'questions=true' : ''
	const payQuery = pay ? 'pay=true' : ''

	try {
		const response = await fetch(
			`${GH_JOBS_ENDPOINT}/${listingId}?${questionsQuery}&${payQuery}`
		)
		const data = await response.json()
		if (!data || data.status === 404) return { career: null }
		const allDepartments: Array<DepartmentType> = await getAllDepartments()
		return { career: formatGreenhouseCareerListing(data, allDepartments) }
	} catch (e) {
		console.error(
			`Error retrieving career listing ${listingId} from Greenhouse: ${e}`
		)
		return { career: null }
	}
}

// formatting functions
export function formatGreenhouseCareerListing(
	greenhouseCareerListing: BaseGreenhouseCareerListingResponse,
	allDepartments: Array<DepartmentType>
): CareerListing {
	const {
		metadata,
		location,
		education,
		content,
		id,
		absolute_url,
		title,
		requisition_id,
		data_compliance,
	} = greenhouseCareerListing

	function getMetadataItem(item: CareerListingMetadata['name']) {
		return metadata.find((meta) => meta.name === item)?.value
	}

	const formattedDepartment = formatDepartment(
		getMetadataItem('Careers Page Department') ?? null,
		allDepartments
	)

	const hasEducation = !!education

	return {
		data_compliance,
		absolute_url,
		id,
		title,
		requisition_id,
		content: content || null,
		department: formattedDepartment,
		product: getMetadataItem('Career Page Product') ?? null,
		employeeType: getMetadataItem('Employee Type') ?? null,
		location: location.name,
		workplaceType: getMetadataItem('Workplace Type') ?? null,
		questions: greenhouseCareerListing.questions ?? null,
		hashi_edu_questions: hasEducation ? EDUCATION_QUESTIONS : ([] as const),
		location_questions: greenhouseCareerListing.location_questions ?? null,
		demographic_questions:
			greenhouseCareerListing.demographic_questions ?? null,
	}
}

function formatDepartment(
	departmentName: string | null,
	allDepartments: Array<DepartmentType>
): CareerListingDepartment {
	if (!departmentName)
		return {
			group: null,
			name: null,
		}

	const group = allDepartments.find(
		(department) =>
			department.name === departmentName ||
			department.children.map(({ name }) => name).includes(departmentName) ||
			department.name === DEPARTMENT_MAP[departmentName]
	)

	return {
		group: group?.name || '',
		name: departmentName,
	}
}

/**
 * Proxied request to an API route that posts the form data to Greenhouse
 * Prevents us from leaking our Greenhouse API token to the client, filtering bots and bad requests
 * Error handling left for the caller
 */
export const postFormDataToProxy = async (
	formData: FormData,
	listingId: string
) => {
	const endpoint = `/${GH_PROXY_ENDPOINT}?listingId=${listingId}`
	const response = await fetch(endpoint, {
		method: 'POST',
		body: formData,
	})

	if (!response.ok) {
		const errorMessage = await response.text()
		throw new Error(`Failed to submit application form:\n${errorMessage}`)
	}

	return response
}

export async function getAllCareers() {
	return []
}

export function isValidEmail(value: string): boolean {
	// Source: https://html.spec.whatwg.org/multipage/input.html#email-state-(type=email)
	// Slightly modified to require domains instead of bare TLDs
	/* eslint-disable-next-line no-useless-escape */
	return /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/i.test(
		value
	)
}

/*
 * Validates if a string is in the format of a month input (YYYY-MM)
 *
 * The regex pattern checks for:
 * - ^ - Start of the string
 * - \d{4} - Exactly 4 digits (representing the year)
 * - - A hyphen character
 * - \d{2} - Exactly 2 digits (representing the month)
 * - $ - End of the string
 *
 * This ensures the string is in the format required for HTML month inputs
 */
const isMonthInput = (value: string): value is MonthInput => {
	return /^\d{4}-\d{2}$/.test(value)
}

type Comparator = '===' | '!==' | '>' | '>=' | '<' | '<='
type MonthInput = `${number}-${number}`

type CompareMonthsResult = {
	value?: boolean
	error?: string
}

export const compareMonths = (
	date1: string,
	date2: string,
	comparator: Comparator
): CompareMonthsResult => {
	if (!isMonthInput(date1)) {
		return {
			error: `Invalid month input: ${date1} is in incorrect format.`,
		}
	}
	if (!isMonthInput(date2)) {
		return {
			error: `Invalid month input: ${date2} is in incorrect format.`,
		}
	}
	// We need to add a day to make it a valid date string
	const parseMonthInput = (dateStr: string): Date => {
		// Always assume YYYY-MM format and add day
		return new Date(`${dateStr}-01T00:00:00`)
	}

	const date1Obj = parseMonthInput(date1)
	const date2Obj = parseMonthInput(date2)

	switch (comparator) {
		case '===':
			return {
				value: date1Obj.getTime() === date2Obj.getTime(),
			}
		case '!==':
			return {
				value: date1Obj.getTime() !== date2Obj.getTime(),
			}
		case '>':
			return {
				value: date1Obj.getTime() > date2Obj.getTime(),
			}
		case '>=':
			return {
				value: date1Obj.getTime() >= date2Obj.getTime(),
			}
		case '<':
			return {
				value: date1Obj.getTime() < date2Obj.getTime(),
			}
		case '<=':
			return {
				value: date1Obj.getTime() <= date2Obj.getTime(),
			}
		default:
			return {
				error: `Invalid comparator: ${comparator}`,
			}
	}
}

export const isStdQuestion = (
	q: GreenhouseQuestion | GreenhouseDemographicQuestion
): q is GreenhouseQuestion => {
	return 'fields' in q
}

export const isDemographicQuestion = (
	q: GreenhouseQuestion | GreenhouseDemographicQuestion
): q is GreenhouseDemographicQuestion => {
	return 'answer_options' in q
}

const DEMOGRAPHIC_QUESTIONS_IDS = ['30005', '30006', '30007', '30008', '30009']
const DEMOGRAPHIC_QUESTION_PREFIX = 'demographic_answers[][question_id]'
const DEMOGRAPHIC_ANSWER_PREFIX =
	'demographic_answers[][answer_options][][answer_option_id]'

const COMPLIANCE_GENERAL_KEY = 'data_compliance[gdpr_consent_given]'
const COMPLIANCE_PROCESSING_KEY =
	'data_compliance[gdpr_processing_consent_given]'
const COMPLIANCE_RETENTION_KEY = 'data_compliance[gdpr_retention_consent_given]'
const COMPLIANCE_DEMOGRAPHIC_KEY =
	'data_compliance[gdpr_demographic_data_consent_given]'

const REFERRAL_URL_KEY = 'gh_src'
const REFERRAL_FORM_DATA_KEY = 'mapped_url_token'

export const EDUCATION_SCHOOL_NAME_ID_KEY = 'educations[][school_name_id]'
export const EDUCATION_DEGREE_ID_KEY = 'educations[][degree_id]'
export const EDUCATION_DISCIPLINE_ID_KEY = 'educations[][discipline_id]'
export const EDUCATION_START_DATE_MONTH_KEY = 'educations[][start_date][month]'
export const EDUCATION_START_DATE_YEAR_KEY = 'educations[][start_date][year]'
export const EDUCATION_END_DATE_MONTH_KEY = 'educations[][end_date][month]'
export const EDUCATION_END_DATE_YEAR_KEY = 'educations[][end_date][year]'

const isDemographicQuestionKey = (k: string) => {
	return DEMOGRAPHIC_QUESTIONS_IDS.includes(k)
}

const appendCompliance = (formData: FormData) => {
	const consentGiven = formData.get(COMPLIANCE_FIELD_NAME)
	formData.delete(COMPLIANCE_FIELD_NAME)

	formData.append(COMPLIANCE_PROCESSING_KEY, consentGiven.toString())
	formData.append(COMPLIANCE_RETENTION_KEY, consentGiven.toString())
	formData.append(COMPLIANCE_DEMOGRAPHIC_KEY, consentGiven.toString())
}

const hasEducationData = (formData: ApplicationFormFieldsState) => {
	return (
		formData.school &&
		formData.degree &&
		formData.discipline &&
		formData.start_date &&
		formData.end_date
	)
}

type EducationData = {
	schoolNameId: string
	degreeId: string
	disciplineId: string
	startDateMonth: string
	startDateYear: string
	endDateMonth: string
	endDateYear: string
}

// must be called after the education questions have been validated
export const extractEducationData = (
	formData: ApplicationFormFieldsState
): EducationData => {
	const schoolNameId = formData.school
	const degreeId = formData.degree
	const disciplineId = formData.discipline
	const startDate = formData.start_date

	// Extract month and year from date strings in format YYYY-MM
	// The regex /^0/ matches only a leading zero at the beginning of the string
	// This converts months like "01", "02" to "1", "2" as Greenhouse expects
	const startDateMonth = startDate?.toString().split('-')[1]?.replace(/^0/, '')
	const startDateYear = startDate?.toString().split('-')[0]
	const endDate = formData.end_date
	const endDateMonth = endDate?.toString().split('-')[1]?.replace(/^0/, '')
	const endDateYear = endDate?.toString().split('-')[0]

	return {
		schoolNameId,
		degreeId,
		disciplineId,
		startDateMonth,
		startDateYear,
		endDateMonth,
		endDateYear,
	}
}

/**
 * Example of a `multipart/form-data` request to Greenhouse.
 * @see https://developers.greenhouse.io/job-board.html#submit-an-application
 *
 * ```
 * curl -X POST \
 * -H "Content-Type: multipart/form-data" \
 * -H "Authorization: Basic MGQwMzFkODIyN2VhZmE2MWRjMzc1YTZjMmUwNjdlMjQ6" \
 * -F "first_name=Sammy" \
 * -F "last_name=McSamson" \
 * -F "email=sammy@example.com" \
 * -F "phone=3337778888" \
 * -F "location=110 5th Ave New York, NY, 10011" \
 * -F "latitude=40.7376671" \
 * -F "longitude=-73.9929196" \
 * -F "resume=@/path/to/resume/ADA084551.pdf" \
 * -F "cover_letter=@/path/to/coverletter/blah.pdf" \
 * -F "mapped_url_token=token12345" \
 * -F "question_12349_url=http://dropbox.com/dl/attachment.pdf" \
 * -F "question_12349_url_filename=attachment.pdf" \
 * -F "question_12350_content=SGVsbG8sIHdvcmxkIQo=" \
 * -F "question_12350_content_filename=something_else.txt" \
 * -F "demographic_answers[][question_id]=87" \
 * -F "demographic_answers[][answer_options][][answer_option_id]=194" \
 * -F "demographic_answers[][question_id]=88" \
 * -F "demographic_answers[][answer_options][][answer_option_id]=212" \
 * -F "demographic_answers[][answer_options][][text]=Free-form Answer" \
  -F "data_compliance[gdpr_consent_given]=true" \ # `gdpr_consent_given` to be deprecated. Use if your organization doesn't have single-purpose consent configured, otherwise use separate values for processing and retention
  -F "data_compliance[gdpr_processing_consent_given]=true" \
  -F "data_compliance[gdpr_retention_consent_given]=true" \
  -F "data_compliance[gdpr_demographic_data_consent_given]=true" \
 * -F "https://boards-api.greenhouse.io/v1/boards/very_awesome_inc/jobs/127817"
 * ```
 */
export const formatApplicationFormData = async (
	formState: ApplicationFormFieldsState
): Promise<FormData> => {
	const formData = new FormData()
	for (const [k, v] of Object.entries(formState)) {
		if (isDemographicQuestionKey(k) && typeof v === 'string') {
			formData.append(DEMOGRAPHIC_QUESTION_PREFIX, k)
			formData.append(DEMOGRAPHIC_ANSWER_PREFIX, v)
			continue
		}

		const isDirectFileUpload = v instanceof FileList
		if (isDirectFileUpload) {
			for (const file of v) {
				formData.append(k, file)
			}
			continue
		}
		formData.append(k, v)
	}

	const urlParams = new URLSearchParams(window.location.search)
	const referralKey = urlParams.get(REFERRAL_URL_KEY)
	const hasReferral = !!referralKey
	if (hasReferral) {
		formData.append(REFERRAL_FORM_DATA_KEY, referralKey)
	}
	appendCompliance(formData)

	if (hasEducationData(formState)) {
		const {
			schoolNameId,
			degreeId,
			disciplineId,
			startDateMonth,
			startDateYear,
			endDateMonth,
			endDateYear,
		} = extractEducationData(formState)
		formData.append(EDUCATION_SCHOOL_NAME_ID_KEY, schoolNameId)
		formData.append(EDUCATION_DEGREE_ID_KEY, degreeId)
		formData.append(EDUCATION_DISCIPLINE_ID_KEY, disciplineId)
		formData.append(EDUCATION_START_DATE_MONTH_KEY, startDateMonth)
		formData.append(EDUCATION_START_DATE_YEAR_KEY, startDateYear)
		formData.append(EDUCATION_END_DATE_MONTH_KEY, endDateMonth)
		formData.append(EDUCATION_END_DATE_YEAR_KEY, endDateYear)
	}

	return formData
}

export const isRemote = (career: CareerListing): boolean => {
	const workplaceType = career.workplaceType
	return workplaceType && !!workplaceType?.match(/remote/i)
}

export const isInternship = (career: CareerListing): boolean => {
	const hasInternEmployeeType =
		!!career.employeeType &&
		!!(
			career.employeeType?.match(/intern/i) ||
			career.employeeType?.match(/early career/i)
		)

	const hasInternDepartment =
		!!career.department &&
		!!(
			career.department.name?.match(/intern/i) ||
			career.department.name?.match(/early career/i)
		)

	return hasInternEmployeeType || hasInternDepartment
}
